import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { entrepriseService } from '../../lib/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HistoriquePaiementsTab = () => {
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPaiement, setSelectedPaiement] = useState(null);

  useEffect(() => {
    fetchPaiements();
  }, []);

  const fetchPaiements = async () => {
    try {
      setLoading(true);
      const data = await entrepriseService.getAvances();
      // Filtrer uniquement les paiements confirmés (état DECAISSEMENT_CONFIRME)
      const paiementsConfirmes = data.filter(a => 
        a.etat === 'DECAISSEMENT_CONFIRME' || 
        a.etat === 'EN_ATTENTE_PAIE'
      );
      setPaiements(paiementsConfirmes);
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      toast.error('Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
    }
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWalletLabel = (walletType) => {
    const labels = {
      'KULU': 'Kulu',
      'MOMO': 'MoMo',
      'ORANGE_MONEY': 'Orange Money',
      'PAYCARD': 'Paycard',
      'SOUTRA_MONEY': 'Soutra Money',
      'YMO': 'YMO'
    };
    return labels[walletType] || walletType;
  };

  // Filtrer les paiements
  const filteredPaiements = paiements.filter(paiement => {
    const employe = paiement.employe_nom || '';
    const matricule = paiement.matricule || '';
    return employe.toLowerCase().includes(searchTerm.toLowerCase()) ||
           matricule.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Calculer les statistiques
  const totalPaiements = filteredPaiements.length;
  const montantTotal = filteredPaiements.reduce((sum, p) => sum + (p.montant_brut || 0), 0);
  const totalFrais = filteredPaiements.reduce((sum, p) => sum + (p.frais || 0), 0);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Chargement des paiements...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Paiements</p>
                <p className="text-3xl font-bold">{totalPaiements}</p>
              </div>
              <div className="text-4xl opacity-80">💳</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Montant Total Versé</p>
                <p className="text-2xl font-bold">{formatMontant(montantTotal)}</p>
              </div>
              <div className="text-4xl opacity-80">💰</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Total Frais</p>
                <p className="text-2xl font-bold">{formatMontant(totalFrais)}</p>
              </div>
              <div className="text-4xl opacity-80">📊</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recherche et actualisation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <Input
              type="text"
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
            <Button onClick={fetchPaiements} variant="outline">
              🔄 Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Avertissement confidentialité */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start">
            <div className="text-blue-500 text-xl mr-3">🔒</div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Confidentialité des données</p>
              <p>
                Pour protéger la vie privée de vos employés, les <strong>numéros de téléphone des wallets</strong> ne sont pas affichés.
                Seuls les Super Administrateurs y ont accès en cas de besoin.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste des paiements */}
      <Card>
        <CardHeader>
          <CardTitle>📋 Historique des Paiements ({filteredPaiements.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredPaiements.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">💳</div>
              <p className="text-gray-600">Aucun paiement trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Employé</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Montant Versé</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Frais</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Total Déduit</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Wallet</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPaiements.map((paiement) => {
                    const totalDeduit = (paiement.montant_brut || 0) + (paiement.frais || 0);
                    return (
                      <tr key={paiement.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className="text-sm text-gray-900">
                            {formatDate(paiement.date_decaissement || paiement.created_at)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-gray-900">
                              {paiement.employe_nom || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {paiement.matricule || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-green-600">
                            {formatMontant(paiement.montant_brut || 0)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-orange-600">
                            {formatMontant(paiement.frais || 0)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-bold text-gray-900">
                            {formatMontant(totalDeduit)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                              {getWalletLabel(paiement.canal)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedPaiement(paiement)}
                          >
                            👁️ Détails
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Détails Paiement */}
      {selectedPaiement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h3 className="text-lg font-medium">💳 Détails du Paiement</h3>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setSelectedPaiement(null)}
              >
                ✖️ Fermer
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Informations employé */}
              <Card>
                <CardHeader>
                  <CardTitle>👤 Employé</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Nom complet</p>
                      <p className="font-medium">{selectedPaiement.employe_nom}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Matricule</p>
                      <p className="font-medium">{selectedPaiement.matricule}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Détails financiers */}
              <Card>
                <CardHeader>
                  <CardTitle>💰 Détails Financiers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Montant versé à l'employé:</span>
                      <span className="font-bold text-green-600">
                        {formatMontant(selectedPaiement.montant_brut)}
                      </span>
                    </div>
                    <div className="flex justify-between border-b pb-2">
                      <span className="text-gray-600">Frais de transaction:</span>
                      <span className="font-medium text-orange-600">
                        {formatMontant(selectedPaiement.frais)}
                      </span>
                    </div>
                    <div className="flex justify-between pt-2">
                      <span className="text-gray-900 font-medium">Total déduit du solde:</span>
                      <span className="font-bold text-gray-900 text-lg">
                        {formatMontant((selectedPaiement.montant_brut || 0) + (selectedPaiement.frais || 0))}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Informations paiement */}
              <Card>
                <CardHeader>
                  <CardTitle>📋 Informations du Paiement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Date du paiement</p>
                      <p className="font-medium">
                        {formatDate(selectedPaiement.date_decaissement || selectedPaiement.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Type de wallet</p>
                      <p className="font-medium">{getWalletLabel(selectedPaiement.canal)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Période de remboursement</p>
                      <p className="font-medium">{selectedPaiement.periode_remboursement || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ID du paiement</p>
                      <p className="font-medium text-xs">{selectedPaiement.id}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Note confidentialité */}
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <div className="flex items-start">
                    <div className="text-yellow-500 text-xl mr-3">🔒</div>
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium mb-1">Confidentialité</p>
                      <p>
                        Le numéro de téléphone du wallet n'est pas affiché pour protéger la vie privée de l'employé.
                        Seuls les Super Administrateurs peuvent accéder à cette information si nécessaire.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoriquePaiementsTab;
